/**
 * LoginPage — email (6-digit OTP code) + Google SSO.
 *
 * №14.1.1-C: this was the SIXTH orphan design system — Tailwind utilities, raw
 * hex (`#0a0a0f` glassmorphism, blue-600 buttons), the multicolour Pattern-Blocks
 * mark, and no theme awareness at all (light and dark rendered identically). It
 * now renders from the ONE component layer (`app-chrome.css`, via `.appshell
 * .appshell--auth`), carries the Ghost-Grid K in the shell accent (D-DS3 / R2),
 * and follows the light/dark setting like every other surface (D-SH4:
 * both-or-neither).
 */

import { useState } from "react";
import { useSearchParams } from "react-router";
import { useAuth } from "../hooks/use-auth";
import { useV3Theme } from "../../v3/hooks/use-v3-theme";
import { GhostGridK } from "../../v3/brand/GhostGridK";
import { AuthShell } from "./AuthShell";

type Step = "email" | "code";

// №11.5 (D-ADM4) — the vertical shell themes a workspace-hinted login may opt into
// (mirrors shell-themes.css). An unknown/absent hint → neutral Kuvio.
const KNOWN_SHELL_THEMES = new Set(["kuvio-legal", "ink", "chancery"]);

export function LoginPage() {
  const { login, requestOtp, verifyOtp } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteEmail = searchParams.get("email") ?? "";
  // №11.5 (D-ADM4) — a context-less signup gets the NEUTRAL Kuvio login (no shell
  // hint → shellTheme undefined → monochrome, the "green login" fix). A
  // workspace-hinted link (branded OTP / magic-link / invite carrying `?shell=`)
  // opts back into that vertical's skin. Only a WHITELISTED theme is honoured —
  // never raw input as a data-attribute — and the light/dark preference is
  // followed as before.
  const shellHint = searchParams.get("shell");
  const shellTheme = shellHint && KNOWN_SHELL_THEMES.has(shellHint) ? shellHint : undefined;
  useV3Theme();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState(inviteEmail);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    setError(null);
    const result = await requestOtp(email);
    setLoading(false);
    if (result.ok && result.challengeId) {
      setChallengeId(result.challengeId);
      setStep("code");
      setCode("");
    } else if (result.error === "rate_limited") {
      setError("Too many code requests. Please wait a few minutes and try again.");
    } else {
      setError("Couldn't send a code. Please try again.");
    }
  };

  const submitCode = async (value: string) => {
    if (!challengeId || value.length !== 6) return;
    setLoading(true);
    setError(null);
    const result = await verifyOtp(challengeId, value);
    setLoading(false);
    if (result.ok) {
      // Auth state updates internally; the app re-renders to the authed view.
      return;
    }
    if (result.error === "invalid_code") {
      const left = result.attemptsRemaining ?? 0;
      setError(`That code didn't match.${left > 0 ? ` ${left} attempt${left === 1 ? "" : "s"} left.` : ""}`);
    } else if (result.error === "locked") {
      setError("Too many attempts. Request a new code.");
    } else if (result.error === "expired") {
      setError("That code expired. Request a new one.");
    } else {
      setError("Sign-in failed. Please try again.");
    }
    setCode("");
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitCode(code);
  };

  const handleResend = async () => {
    setLoading(true);
    setError(null);
    const result = await requestOtp(email);
    setLoading(false);
    if (result.ok && result.challengeId) {
      setChallengeId(result.challengeId);
      setCode("");
    } else {
      setError("Couldn't resend a code. Please try again.");
    }
  };

  const backToEmail = () => {
    setStep("email");
    setChallengeId(null);
    setCode("");
    setError(null);
  };

  return (
    <AuthShell shellTheme={shellTheme}>
      <div className="authpane__card">
        <div className="authpane__brand">
          <GhostGridK size={44} />
          <span className="brand__name"><span className="b1">Kuvio</span></span>
        </div>
        <div>
          <h1>Sign in</h1>
          <p className="authpane__sub">
            {step === "email"
              ? "Enter your email, or sign in with Google."
              : `Enter the 6-digit code sent to ${email}.`}
          </p>
        </div>

        {step === "email" ? (
          <>
            <form onSubmit={handleRequestCode}>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                aria-label="Email address"
              />
              <button type="submit" className="btn btn--primary" disabled={loading}>
                {loading ? "Sending…" : "Continue with email"}
              </button>
            </form>

            {error && <p className="authpane__err">{error}</p>}

            <div className="authpane__or"><span>or</span></div>

            <button className="btn btn--secondary" onClick={() => login("google")}>
              <GoogleMark />
              Sign in with Google
            </button>
          </>
        ) : (
          <>
            <form onSubmit={handleVerify}>
              <input
                className="authcode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                autoFocus
                aria-label="6-digit code"
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
                  setCode(v);
                  if (v.length === 6) void submitCode(v);
                }}
              />
              <button type="submit" className="btn btn--primary" disabled={loading || code.length !== 6}>
                {loading ? "Verifying…" : "Verify"}
              </button>
            </form>

            {error && <p className="authpane__err">{error}</p>}

            <div className="authpane__foot">
              <button className="btn btn--ghost btn--sm" onClick={backToEmail}>← Use a different email</button>
              <button className="btn btn--ghost btn--sm" onClick={handleResend} disabled={loading}>Resend code</button>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  );
}

/** Google's mark keeps its own brand colours — it is Google's asset, not ours. */
function GoogleMark() {
  return (
    <svg className="g" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}
